import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type { TUserWorkHours } from "@/types/user";

export class WorkHoursStore {
  workHours: TUserWorkHours | null = null;
  isLoading = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  async fetchWorkHours(): Promise<TUserWorkHours> {
    this.isLoading = true;
    try {
      const data = await this.rootStore.workHoursService.getCurrentUserWorkHours();
      runInAction(() => {
        this.workHours = data;
      });
      return data;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updateWorkHours(
    patch: Partial<TUserWorkHours>,
    source: AnalyticsSource = "page",
  ): Promise<TUserWorkHours> {
    const previous = this.workHours;
    // Read before the optimistic patch below: `fields` is what this save changed.
    const fields = changedFields(patch, previous ?? undefined);
    if (previous) {
      runInAction(() => {
        this.workHours = { ...previous, ...patch };
      });
    }
    try {
      const updated = await this.rootStore.workHoursService.updateCurrentUserWorkHours(patch);
      runInAction(() => {
        this.workHours = updated;
      });
      if (fields.length > 0) track("work_hours_updated", { fields, source });
      return updated;
    } catch (error) {
      if (previous) {
        runInAction(() => {
          this.workHours = previous;
        });
      }
      throw error;
    }
  }
}
